// New Smadex web pixel. Replaces the per-action image tags in
// `smadexPixelScript` with Smadex's hosted pixel + queue API. Loading the pixel
// and calling `smx('init')` auto-fires landing / engaged_landing / page_load on
// Smadex's side; the only events we send explicitly are the `track` calls below.
// Those cover the signup funnel and the subscribe funnel through to purchase.
// The purchase also still fires the old per-action pixel (order 115908) alongside
// the new event, since that legacy order is what existing reporting is attributed
// against. Purchases are also reported server-to-server by the api's Stripe webhook.
//
// `smadexPixelLoaderScript` returns inline script *content* meant to go inside a
// Ketch consent-gated `<Script type="text/plain" data-purposes=...>` element,
// exactly like the old `smadexPixelScript` did, and returns an empty string when
// disabled so nothing loads. Funnel events do not go through a rendered tag at all;
// see `useSmadexFunnelEvent`.

import {SMADEX_TRACKING_GROUP_ID} from 'notability-services-common-universal'

import {KetchConsentPurpose, consentDeclined, ketchSuppression} from '../ketchConsent'

export {SMADEX_TRACKING_GROUP_ID}

// Smadex's hosted pixel. NOTE: this is pixel.smadex.com, NOT the tracker-tpt
// host used for the server-to-server postbacks.
const SMADEX_PIXEL_SCRIPT_URL = 'https://pixel.smadex.com/pixel.min.js'

// The funnel events Smadex maps on their side. Kept deliberately narrow: if it
// is not one of these, we do not fire a client track call.
export type SmadexFunnelEvent =
  | 'signupinitiated'
  | 'signupcompleted'
  | 'subscriptionpagelanding'
  | 'subscriptionselected'
  | 'purchase'

/**
 * Smadex counts a conversion once when the pixel and the S2S postback both carry the same
 * `event_id`; without it the two reports of one purchase are two conversions.
 */
export type SmadexEventParams = {value?: number; currency?: string; event_id?: string}

/**
 * Loader for the new Smadex pixel. Injects the `smx` queue stub, loads
 * `pixel.min.js` for our tracking group, and calls `smx('init')` (which
 * auto-fires landing / engaged_landing / page_load). Verbatim vendor snippet
 * from the onboarding deck; do not "tidy" it. Render once per page, before any
 * `smadexTrackScript`. Returns '' when disabled.
 */
export function smadexPixelLoaderScript({enabled}: {enabled: boolean}): string {
  if (!enabled) {
    return ''
  }
  return `var SMADEX_TRACKING_GROUP='${SMADEX_TRACKING_GROUP_ID}';!function(w,d){var t=["${SMADEX_PIXEL_SCRIPT_URL}"];if(!w.smx){var a=w.smx=function(){a.performOperation?a.performOperation.apply(a,arguments):a.operationQueue.push(arguments)};a.operationQueue=[],a.smxTrackGroupId=SMADEX_TRACKING_GROUP;for(var n=d.getElementsByTagName("script")[0],o=0;o<t.length;o++){var i=d.createElement("script");i.async=!0,i.src=t[o],n.parentNode.insertBefore(i,n)}}}(window,document);smx('init');`
}

declare global {
  interface Window {
    smx?: ((...args: unknown[]) => void) & {performOperation?: unknown}
  }
}

/**
 * Whether `pixel.min.js` has landed and drained the loader's queue, which is the earliest
 * point at which a queued `track` has actually left the browser. Callers waiting on a track
 * before they navigate away need this rather than the loader's `<script>` element, which is
 * server-rendered and present from first paint whether or not consent un-gated it.
 */
export function smadexPixelHasLoaded(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return typeof window.smx?.performOperation === 'function'
}

/**
 * Whether a page that reports an event and then navigates away can stop waiting: either a
 * beacon has actually left the browser, or none is ever coming.
 *
 * `expectingReport` must be the same condition the caller passed to `useSmadexFunnelEvent` as
 * `enabled`. Nothing else on the page loads the pixel, so an arrival that reports no event never
 * loads it either, and waiting on it would burn the caller's whole cap on every such arrival.
 * That is the majority of traffic on a page whose event only fires once per account.
 *
 * Do not substitute the presence of the loader's `<script>` element for this. That element is
 * server-rendered and sits in the DOM from first paint whether or not consent un-gated the code
 * inside it, so it reports ready immediately and truncates the very report it is meant to protect.
 *
 * A page that left Ketch out never gets the consent event the pixel loads on, so nothing is coming.
 */
export function smadexReportingSettled({expectingReport}: {expectingReport: boolean}): boolean {
  return (
    !expectingReport ||
    consentDeclined(KetchConsentPurpose.TargetedAdvertising) ||
    ketchSuppression() !== null ||
    smadexPixelHasLoaded()
  )
}

/**
 * Run the loader snippet now, from script rather than from a rendered tag.
 *
 * A page reached by a client-side route change never runs its own
 * `smadexPixelLoaderScript` tag: `beforeInteractive` only executes in the initial
 * server-rendered document, so on those arrivals `window.smx` is undefined and every
 * `track` call silently no-ops. Callers that must fire on any arrival path load it
 * this way instead.
 *
 * Idempotent: the snippet already no-ops when `w.smx` exists, so re-running it on a
 * page whose tag did fire only repeats `smx('init')`.
 */
export function loadSmadexPixel({enabled}: {enabled: boolean}): void {
  if (!enabled || typeof window === 'undefined' || window.smx) {
    return
  }
  const element = document.createElement('script')
  element.text = smadexPixelLoaderScript({enabled})
  document.head.appendChild(element)
}

/**
 * Fire one Smadex funnel event through the pixel's queue stub. Guarded so it is a
 * no-op if the loader did not run (consent denied, ad-blocker); the stub queues the
 * call until `pixel.min.js` drains it.
 */
export function trackSmadexEvent(event: SmadexFunnelEvent, params?: SmadexEventParams): void {
  if (params) {
    window.smx?.('track', event, params)
  } else {
    window.smx?.('track', event)
  }
}
